import type {
  ArchibaldHandoffPort,
  HandoffTarget,
  HorizonCatalogPort,
  HouseholdArtifactsPort,
  MealSuggestionPort,
  PersistencePort,
  RecipeReturnRequest,
  RecipeReturnVerifierPort,
} from './contracts';
import { buildHandoffUniversalLink } from '@dohm/handoff';
import {
  abandonReservedContainer,
  confirmAuditPresent,
  confirmContainer,
  consumePartial,
  consumeTotal,
  correctContainer,
  createDraftContainer,
  discardContainer,
  finalizeReservedContainer,
  markAuditMissing,
  moveContainer,
  restoreContainer,
  undoEvent,
} from '../domain/inventory';
import { suggestMeal, type MealSuggestion, type MealTemplate } from '../domain/meals';
import { MAX_RECIPE_SHOPPING_LISTS } from '../domain/model';
import type {
  AuditRecord,
  CreateContainerInput,
  DeclareTemperatureIncidentInput,
  FoodContainer,
  Freezer,
  HouseholdPreferences,
  InventoryEvent,
  InventoryBatchMutation,
  InventoryMutation,
  InventorySnapshot,
  IsoDate,
  QuantityUnit,
  RecipeShoppingList,
  RecordTemperatureReadingInput,
  ResolveIncidentExposureInput,
  SetupInput,
  StorageNode,
  HouseholdDeletionResult,
} from '../domain/model';
import { createDemoContainers } from '../content/demo';
import {
  hasVisibleNumber,
  holdsVisibleNumber,
  selectRestorationAssignment,
} from '../domain/visible-number';
import { previewIncidentExposure } from '../domain/temperature';
import {
  buildRecipeShoppingList,
  recordRecipeShoppingListHandoffReceipt,
  setRecipeShoppingItemChecked,
} from '../domain/recipe-journey';

const randomId = (): string => crypto.randomUUID();
const today = (): IsoDate => new Date().toISOString().slice(0, 10);
const sha256 = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};
const safeExternalUrl = (value: string): URL | null => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      url.username === '' &&
      url.password === '' &&
      url.search === '' &&
      url.hash === ''
        ? url
        : null;
  } catch {
    return null;
  }
};

export type RecipeReturnImportResult =
  | { state: 'imported' | 'already_imported'; list: RecipeShoppingList }
  | { state: 'demo_forbidden' | 'expired' | 'replayed' | 'invalid' | 'limit_reached' | 'unavailable' };

export type ArchibaldHandoffResult =
  | { state: 'ready'; url: string; intentId: string; expiresAt: string }
  | {
      state: 'demo_forbidden' | 'unavailable' | 'trial_eligible' | 'trial_expired' | 'declined';
      fallbackUrl: string | null;
    };

export class InventoryService {
  constructor(
    private readonly repository: PersistencePort,
    private readonly horizonCatalog: HorizonCatalogPort,
    private readonly mealSuggestionPort: MealSuggestionPort,
    private readonly householdArtifacts: HouseholdArtifactsPort = {
      deleteForHousehold: () => Promise.resolve(),
    },
    private readonly recipeReturnVerifier: RecipeReturnVerifierPort = {
      available: false,
      verifyOnce: () => Promise.resolve({ state: 'unavailable' }),
    },
    private readonly archibaldHandoff: ArchibaldHandoffPort = {
      available: false,
      status: () => Promise.resolve({ state: 'disconnected' }),
      connect: () => Promise.resolve({
        state: 'fallback',
        access: 'unavailable',
        fallbackUrl: null,
      }),
      disconnect: () => Promise.resolve(),
      prepare: () => Promise.resolve({
        state: 'fallback',
        access: 'unavailable',
        fallbackUrl: null,
      }),
      completeKeyExchange: () => Promise.resolve({ state: 'unavailable' }),
      retrieveReceipt: () => Promise.resolve({ state: 'none', intentId: null }),
      verifyReceipt: () => Promise.resolve({ state: 'unavailable', intentId: null }),
    },
  ) {}

  async initialize(): Promise<void> {
    await this.repository.initialize();
    for (const householdId of await this.repository.pendingHouseholdArtifactDeletions()) {
      await this.householdArtifacts.deleteForHousehold(householdId);
      await this.repository.completeHouseholdArtifactDeletion(householdId);
    }
    await this.repository.reconcileRotationAlerts(today());
  }

  async setRotationAlertState(
    containerId: string,
    threshold: FoodContainer['rotationAlerts'][number]['threshold'],
    state: FoodContainer['rotationAlerts'][number]['state'],
  ): Promise<void> {
    await this.repository.setRotationAlertState(containerId, threshold, state);
  }

  async snapshot(): Promise<InventorySnapshot> {
    await this.repository.reconcileRotationAlerts(today());
    return this.repository.loadSnapshot();
  }

  setup(input: SetupInput): Promise<InventorySnapshot> {
    return this.repository.setup(input);
  }

  addFreezer(name: string, equipmentKind: Freezer['equipmentKind']): Promise<Freezer> {
    return this.repository.addFreezer(name, equipmentKind);
  }

  addLocation(freezerId: string, name: string, parentId: string | null): Promise<StorageNode> {
    return this.repository.addLocation(freezerId, name, parentId);
  }

  async createContainer(
    input: CreateContainerInput,
    idempotencyKey = `create:${randomId()}`,
  ): Promise<InventoryMutation> {
    return this.repository.createDraft(input, idempotencyKey, createDraftContainer);
  }

  confirm(container: FoodContainer): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `confirm:${container.id}:${container.version}`,
      confirmContainer,
      container.version,
    );
  }

  finalizeReservation(
    container: FoodContainer,
    locationId: string,
  ): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `finalize:${container.id}:${container.version}:${locationId}`,
      (current, context) => finalizeReservedContainer(current, locationId, context),
      container.version,
    );
  }

  abandonReservation(container: FoodContainer): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `abandon:${container.id}:${container.version}`,
      abandonReservedContainer,
      container.version,
    );
  }

  async duplicate(container: FoodContainer): Promise<InventoryMutation> {
    return this.createContainer({
      name: container.name,
      barcode: container.barcode ?? null,
      categoryId: container.categoryId,
      locationId: container.locationId,
      quantity: container.initialQuantity,
      unit: container.unit,
      storageStartedOn: today(),
      purchasedOn: null,
      openedOn: null,
      useFirstOn: null,
      manufacturerDateType: container.manufacturerDateType,
      manufacturerDate: container.manufacturerDate,
      horizonRule: container.horizonRule,
      qualityHorizonOn: null,
      notes: container.notes,
    });
  }

  consumeAll(container: FoodContainer): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `total:${container.id}:${container.version}`,
      consumeTotal,
      container.version,
    );
  }

  consumePart(
    container: FoodContainer,
    input: {
      mode: 'remaining' | 'used';
      value: number;
      unit: QuantityUnit;
      replacementLocationId: string;
    },
  ): Promise<InventoryMutation> {
    const key = [
      'partial',
      container.id,
      container.version,
      input.mode,
      input.value,
      input.unit,
      input.replacementLocationId,
    ].join(':');
    return this.repository.mutateContainer(container.id, key, (current, context) =>
      consumePartial(current, input, context), container.version,
    );
  }

  move(container: FoodContainer, locationId: string): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `move:${container.id}:${container.version}:${locationId}`,
      (current, context) => moveContainer(current, locationId, context),
      container.version,
    );
  }

  discard(container: FoodContainer, reason: string): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `discard:${container.id}:${container.version}`,
      (current, context) => discardContainer(current, reason, context),
      container.version,
    );
  }

  restore(
    container: FoodContainer,
    locationId: string,
    quantity: number | null,
  ): Promise<InventoryMutation> {
    return this.repository.reactivateContainer(
      container.id,
      `restore:${container.id}:${container.version}:${locationId}:${quantity ?? 'none'}`,
      (current, assignment, context) =>
        restoreContainer(current, locationId, quantity, assignment, context),
      container.version,
    );
  }

  correct(
    container: FoodContainer,
    updates: Parameters<typeof correctContainer>[1],
  ): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `correct:${container.id}:${container.version}:${randomId()}`,
      (current, context) => correctContainer(current, updates, context),
      container.version,
    );
  }

  confirmPresent(container: FoodContainer): Promise<InventoryMutation> {
    return this.repository.mutateContainer(
      container.id,
      `audit-confirm:${container.id}:${container.version}`,
      confirmAuditPresent,
      container.version,
    );
  }

  async undo(event: InventoryEvent): Promise<InventoryMutation> {
    if (event.containerId == null) {
      throw new Error('Cet événement ne cible aucun contenant.');
    }
    const reactivatesReleasedNumber =
      event.payload.before != null &&
      event.payload.after != null &&
      holdsVisibleNumber(event.payload.before) &&
      !holdsVisibleNumber(event.payload.after);
    if (reactivatesReleasedNumber) {
      return this.repository.reactivateContainer(
        event.containerId,
        `undo:${event.id}`,
        (current, assignment, context) => undoEvent(current, event, context, assignment),
      );
    }
    return this.repository.mutateContainer(event.containerId, `undo:${event.id}`, (current, context) =>
      undoEvent(current, event, context),
    );
  }

  async completeAudit(input: {
    freezerId: string;
    locationId: string;
    seenSerials: number[];
    unknownSerials: number[];
  }): Promise<AuditRecord> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) {
      throw new Error('Foyer local introuvable.');
    }
    const expected = snapshot.containers
      .filter(hasVisibleNumber)
      .filter(
        (container) =>
        container.locationId === input.locationId &&
        ['present', 'partial'].includes(container.status),
      );
    const seenSet = new Set(input.seenSerials);
    const missing = expected.filter((container) => !seenSet.has(container.serialNumber));
    const seen = expected.filter((container) => seenSet.has(container.serialNumber));
    const auditId = randomId();

    for (const container of missing) {
      await this.repository.mutateContainer(
        container.id,
        `audit-missing:${auditId}:${container.id}`,
        markAuditMissing,
      );
    }
    for (const container of seen) {
      await this.repository.mutateContainer(
        container.id,
        `audit-confirm:${auditId}:${container.id}`,
        confirmAuditPresent,
      );
    }

    const record: AuditRecord = {
      id: auditId,
      householdId: snapshot.household.id,
      freezerId: input.freezerId,
      locationId: input.locationId,
      expectedCycleIds: expected.map((container) => container.id),
      seenCycleIds: seen.map((container) => container.id),
      missingCycleIds: missing.map((container) => container.id),
      expectedSerials: expected.map((container) => container.serialNumber),
      seenSerials: seen.map((container) => container.serialNumber),
      missingSerials: missing.map((container) => container.serialNumber),
      unknownSerials: [...new Set(input.unknownSerials)].sort((left, right) => left - right),
      completedAt: new Date().toISOString(),
    };
    await this.repository.saveAudit(record);
    return record;
  }

  async savePreferences(
    preferences: Pick<
      HouseholdPreferences,
      'exclusions' | 'alertHorizonDays' | 'lastLocationId' | 'reminderFrequency' | 'reminderRecipient'
    >,
  ): Promise<void> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) {
      throw new Error('Foyer local introuvable.');
    }
    const now = new Date().toISOString();
    await this.repository.mutatePreferences(snapshot.household.id, (current) => ({
      ...current,
      exclusions: [...new Set(preferences.exclusions.map((item) => item.trim()).filter(Boolean))],
      alertHorizonDays: preferences.alertHorizonDays,
      lastLocationId: preferences.lastLocationId,
      reminderFrequency: preferences.reminderFrequency ?? 'off',
      reminderRecipient: preferences.reminderRecipient ?? 'in-app',
      updatedAt: now,
    }));
  }

  async markReminderDelivered(deliveredOn: string): Promise<void> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) return;
    await this.repository.mutatePreferences(snapshot.household.id, (current) => ({
      ...current,
      lastReminderOn: deliveredOn,
      updatedAt: new Date().toISOString(),
    }));
  }

  savePersistentStorageStatus(
    status: InventorySnapshot['meta']['persistentStorage'],
  ): Promise<void> {
    return this.repository.savePersistentStorageStatus(status);
  }

  mealSuggestions(
    snapshot: InventorySnapshot,
    templates: MealTemplate[],
  ): MealSuggestion[] {
    return this.mealSuggestionPort.suggest(
      snapshot.containers,
      templates,
      snapshot.preferences?.exclusions ?? [],
      today(),
    );
  }

  mealSuggestion(
    snapshot: InventorySnapshot,
    template: MealTemplate,
    portions: number,
  ): MealSuggestion | null {
    return suggestMeal(
      snapshot.containers,
      template,
      snapshot.preferences?.exclusions ?? [],
      today(),
      portions,
    );
  }

  async importRecipeReturn(
    request: RecipeReturnRequest,
    templates: MealTemplate[],
    mode: 'app' | 'demo',
  ): Promise<RecipeReturnImportResult> {
    if (mode === 'demo') return { state: 'demo_forbidden' };
    const snapshot = await this.snapshot();
    if (snapshot.household == null || snapshot.preferences == null) {
      return { state: 'invalid' };
    }
    const sourceReturnDigest = await sha256(
      `${request.publicRecipeId}\n${request.returnToken}`,
    );
    const existing = (snapshot.preferences.recipeShoppingLists ?? []).find(
      (list) => list.sourceReturnDigest === sourceReturnDigest,
    );
    if (existing != null) return { state: 'already_imported', list: existing };
    if ((snapshot.preferences.recipeShoppingLists ?? []).length >= MAX_RECIPE_SHOPPING_LISTS) {
      return { state: 'limit_reached' };
    }
    if (!this.recipeReturnVerifier.available) return { state: 'unavailable' };

    const verification = await this.recipeReturnVerifier.verifyOnce(request, sourceReturnDigest);
    if (verification.state !== 'accepted') return verification;
    const occurredAt = Date.parse(verification.occurredAt);
    if (
      verification.publicRecipeId !== request.publicRecipeId ||
      verification.idempotencyKey !== sourceReturnDigest ||
      typeof verification.idempotent !== 'boolean' ||
      !Number.isFinite(occurredAt)
    ) {
      return { state: 'invalid' };
    }
    return this.persistRecipeShoppingList(
      snapshot,
      request,
      templates,
      sourceReturnDigest,
    );
  }

  async importEditorialRecipeReturn(
    request: Pick<RecipeReturnRequest, 'publicRecipeId' | 'locale'>,
    templates: MealTemplate[],
    mode: 'app' | 'demo',
    confirmed: true,
  ): Promise<RecipeReturnImportResult> {
    if (mode === 'demo') return { state: 'demo_forbidden' };
    if (confirmed !== true) return { state: 'invalid' };
    const snapshot = await this.snapshot();
    if (snapshot.household == null || snapshot.preferences == null) {
      return { state: 'invalid' };
    }
    const sourceReturnDigest = await sha256(
      `editorial-selection-v1\n${request.publicRecipeId}\n${request.locale}`,
    );
    const existing = (snapshot.preferences.recipeShoppingLists ?? []).find(
      (list) => list.sourceReturnDigest === sourceReturnDigest,
    );
    if (existing != null) return { state: 'already_imported', list: existing };
    if ((snapshot.preferences.recipeShoppingLists ?? []).length >= MAX_RECIPE_SHOPPING_LISTS) {
      return { state: 'limit_reached' };
    }
    return this.persistRecipeShoppingList(
      snapshot,
      request,
      templates,
      sourceReturnDigest,
    );
  }

  private async persistRecipeShoppingList(
    snapshot: InventorySnapshot,
    request: Pick<RecipeReturnRequest, 'publicRecipeId' | 'locale'>,
    templates: MealTemplate[],
    sourceReturnDigest: string,
  ): Promise<RecipeReturnImportResult> {
    if (snapshot.household == null || snapshot.preferences == null) {
      return { state: 'invalid' };
    }
    const template = templates.find((candidate) => candidate.id === request.publicRecipeId);
    if (template == null) return { state: 'invalid' };
    const suggestion = this.mealSuggestion(snapshot, template, template.defaultPortions ?? 2);
    if (suggestion == null) return { state: 'invalid' };

    const now = new Date().toISOString();
    const candidate = buildRecipeShoppingList({
      householdId: snapshot.household.id,
      id: crypto.randomUUID(),
      locale: request.locale,
      sourceReturnDigest,
      suggestion,
      now,
    });
    const preferences = await this.repository.mutatePreferences(
      snapshot.household.id,
      (current) => {
        const currentLists = current.recipeShoppingLists ?? [];
        const duplicate = currentLists.find(
          (list) => list.sourceReturnDigest === sourceReturnDigest,
        );
        if (duplicate != null) return current;
        return {
          ...current,
          recipeShoppingLists: [candidate, ...currentLists],
          updatedAt: now,
        };
      },
    );
    const saved = (preferences.recipeShoppingLists ?? []).find(
      (list) => list.sourceReturnDigest === sourceReturnDigest,
    );
    if (saved == null) return { state: 'invalid' };
    return { state: saved.id === candidate.id ? 'imported' : 'already_imported', list: saved };
  }

  handoffAvailable(): boolean {
    return this.archibaldHandoff.available;
  }

  archibaldHandoffStatus(mode: 'app' | 'demo', target: HandoffTarget = 'archibald') {
    if (mode === 'demo' || !this.archibaldHandoff.available) {
      return Promise.resolve({ state: 'disconnected' as const });
    }
    return this.archibaldHandoff.status(target);
  }

  connectArchibaldHandoff(email: string, mode: 'app' | 'demo') {
    if (mode === 'demo' || !this.archibaldHandoff.available) {
      return Promise.resolve({
        state: 'fallback' as const,
        access: 'unavailable' as const,
        fallbackUrl: null,
      });
    }
    return this.archibaldHandoff.connect(email);
  }

  disconnectArchibaldHandoff() {
    return this.archibaldHandoff.disconnect();
  }

  completeArchibaldKeyExchange(mode: 'app' | 'demo') {
    if (mode === 'demo' || !this.archibaldHandoff.available) {
      return Promise.resolve({ state: 'unavailable' as const });
    }
    return this.archibaldHandoff.completeKeyExchange();
  }

  async retrieveArchibaldReceipt(listId: string, mode: 'app' | 'demo') {
    if (mode === 'demo' || !this.archibaldHandoff.available) {
      return { state: 'none' as const, intentId: null };
    }
    const receipt = await this.archibaldHandoff.retrieveReceipt(listId);
    if (receipt.state === 'accepted') {
      await this.persistAcceptedArchibaldReceipt(listId, receipt);
    }
    return receipt;
  }

  verifyArchibaldReceipt(receipt: unknown, mode: 'app' | 'demo', target: HandoffTarget = 'archibald') {
    if (mode === 'demo' || !this.archibaldHandoff.available) {
      return Promise.resolve({ state: 'unavailable' as const, intentId: null });
    }
    return this.archibaldHandoff.verifyReceipt(receipt, target);
  }

  async exportArchibaldReceipt(listId: string | null, mode: 'app' | 'demo', intentId?: string): Promise<string> {
    if (mode === 'demo' || !this.archibaldHandoff.available || !this.archibaldHandoff.exportReceipt) {
      throw new Error('HANDOFF_RECEIPT_EXPORT_UNAVAILABLE');
    }
    if (intentId) return this.archibaldHandoff.exportReceipt(listId ?? '', { intentId, target: 'archibald' });
    const snapshot = await this.snapshot();
    const list = snapshot.preferences?.recipeShoppingLists?.find(candidate => candidate.id === listId);
    if (!list) throw new Error('RECIPE_SHOPPING_LIST_NOT_FOUND');
    return this.archibaldHandoff.exportReceipt(list.id, list.handoffReceipt);
  }

  async setRecipeShoppingItemChecked(
    listId: string,
    itemId: string,
    checked: boolean,
  ): Promise<RecipeShoppingList> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) throw new Error('Foyer local introuvable.');
    const now = new Date().toISOString();
    const preferences = await this.repository.mutatePreferences(
      snapshot.household.id,
      (current) => {
        const currentLists = current.recipeShoppingLists ?? [];
        const list = currentLists.find((candidate) => candidate.id === listId);
        if (list == null) throw new Error('RECIPE_SHOPPING_LIST_NOT_FOUND');
        return {
          ...current,
          recipeShoppingLists: currentLists.map((candidate) =>
            candidate.id === listId
              ? setRecipeShoppingItemChecked(candidate, itemId, checked, now)
              : candidate,
          ),
          updatedAt: now,
        };
      },
    );
    return (preferences.recipeShoppingLists ?? []).find((candidate) => candidate.id === listId)!;
  }

  async releaseRecipeShoppingList(listId: string): Promise<string> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) throw new Error('Foyer local introuvable.');
    const now = new Date().toISOString();
    await this.repository.mutatePreferences(snapshot.household.id, (current) => {
      const currentLists = current.recipeShoppingLists ?? [];
      const list = currentLists.find((candidate) => candidate.id === listId);
      if (list == null) throw new Error('RECIPE_SHOPPING_LIST_NOT_FOUND');
      if (
        list.handoffReceipt?.status !== 'accepted' ||
        list.handoffReceipt.signatureVerified !== true ||
        list.handoffReceipt.sourceMayRelease !== true
      ) {
        throw new Error('RECIPE_SHOPPING_LIST_NOT_RELEASED');
      }
      return {
        ...current,
        recipeShoppingLists: currentLists.filter((candidate) => candidate.id !== listId),
        updatedAt: now,
      };
    });
    return listId;
  }

  private async persistAcceptedArchibaldReceipt(
    listId: string,
    receipt: { readonly intentId: string; readonly target: HandoffTarget; readonly verifiedAt: string },
  ): Promise<void> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null) throw new Error('Foyer local introuvable.');
    await this.repository.mutatePreferences(snapshot.household.id, (current) => {
      const currentLists = current.recipeShoppingLists ?? [];
      const list = currentLists.find((candidate) => candidate.id === listId);
      if (list == null) throw new Error('RECIPE_SHOPPING_LIST_NOT_FOUND');
      if (
        list.handoffReceipt?.intentId === receipt.intentId &&
        list.handoffReceipt.verifiedAt === receipt.verifiedAt
      ) {
        return current;
      }
      return {
        ...current,
        recipeShoppingLists: currentLists.map((candidate) =>
          candidate.id === listId
            ? recordRecipeShoppingListHandoffReceipt(candidate, receipt, receipt.target)
            : candidate,
        ),
        updatedAt: receipt.verifiedAt,
      };
    });
  }

  async prepareArchibaldHandoff(
    listId: string,
    mode: 'app' | 'demo',
    target: HandoffTarget = 'archibald',
  ): Promise<ArchibaldHandoffResult> {
    if (mode === 'demo') return { state: 'demo_forbidden', fallbackUrl: null };
    const snapshot = await this.snapshot();
    const list = snapshot.preferences?.recipeShoppingLists?.find(
      (candidate) => candidate.id === listId,
    );
    if (list == null) return { state: 'unavailable', fallbackUrl: null };
    if (!this.archibaldHandoff.available) {
      return { state: 'unavailable', fallbackUrl: null };
    }
    const prepared = await this.archibaldHandoff.prepare({ list, confirmed: true, target });
    if (prepared.state === 'ready') {
      const baseUrl = safeExternalUrl(prepared.baseUrl);
      if (
        baseUrl == null ||
        prepared.token.length > 4_096 ||
        !/^[A-Za-z0-9_-]{32,4096}$/u.test(prepared.token) ||
        !/^hnd_[A-Za-z0-9_-]{16,124}$/u.test(prepared.intentId) ||
        !Number.isFinite(Date.parse(prepared.expiresAt))
      ) {
        return { state: 'unavailable', fallbackUrl: null };
      }
      return {
        state: 'ready',
        url: buildHandoffUniversalLink(baseUrl.toString(), prepared.token).toString(),
        intentId: prepared.intentId,
        expiresAt: prepared.expiresAt,
      };
    }
    let fallbackUrl: string | null = null;
    if (prepared.fallbackUrl != null) {
      const candidate = safeExternalUrl(prepared.fallbackUrl);
      if (candidate != null) {
        fallbackUrl = candidate.toString();
      }
    }
    return {
      state: prepared.access === 'trial_active' || prepared.access === 'entitled'
        ? 'unavailable'
        : prepared.access,
      fallbackUrl,
    };
  }

  consumeRecipe(
    recipeTitle: string,
    lines: Array<{ containerId: string; amount: number; unit: QuantityUnit }>,
    idempotencyKey = `recipe:${randomId()}`,
  ): Promise<InventoryBatchMutation> {
    const aggregated = new Map<
      string,
      { containerId: string; amount: number; unit: QuantityUnit }
    >();
    for (const line of lines) {
      if (!Number.isFinite(line.amount) || line.amount <= 0) {
        throw new Error('Chaque quantité réellement utilisée doit être strictement positive.');
      }
      const current = aggregated.get(line.containerId);
      if (current != null && current.unit !== line.unit) {
        throw new Error('Une même fiche ne peut pas être déduite dans deux unités différentes.');
      }
      aggregated.set(line.containerId, {
        containerId: line.containerId,
        amount: (current?.amount ?? 0) + line.amount,
        unit: line.unit,
      });
    }
    const effectiveLines = [...aggregated.values()];
    if (effectiveLines.length === 0) {
      throw new Error('Confirmez au moins un aliment réellement utilisé.');
    }
    return this.repository.mutateContainers(
      effectiveLines.map((line) => line.containerId),
      idempotencyKey,
      (containers, _householdContainers, contexts) =>
        containers.map((container, index) => {
          const line = effectiveLines[index]!;
          return consumePartial(
            container,
            {
              mode: 'used',
              value: line.amount,
              unit: line.unit,
              replacementLocationId: container.locationId,
            },
            contexts[index]!,
            `Recette préparée : ${recipeTitle}`,
          );
        }),
    );
  }

  undoRecipe(batch: InventoryBatchMutation): Promise<InventoryBatchMutation> {
    const eventsByContainer = new Map(
      batch.mutations.map((mutation) => [mutation.container.id, mutation.event]),
    );
    const containerIds = batch.mutations.map((mutation) => mutation.container.id);
    return this.repository.mutateContainers(
      containerIds,
      `undo-recipe:${batch.idempotencyKey}`,
      (containers, householdContainers, contexts) => {
        const projected = structuredClone(householdContainers);
        return containers.map((container, index) => {
          const event = eventsByContainer.get(container.id);
          if (event == null) throw new Error('Événement de recette introuvable.');
          const reactivatesReleasedNumber =
            event.payload.before != null &&
            event.payload.after != null &&
            holdsVisibleNumber(event.payload.before) &&
            !holdsVisibleNumber(event.payload.after);
          const assignment = reactivatesReleasedNumber
            ? selectRestorationAssignment(container, projected, contexts[index]!.now)
            : null;
          const mutation = undoEvent(
            container,
            event,
            contexts[index]!,
            assignment,
          );
          const projectedIndex = projected.findIndex((item) => item.id === container.id);
          if (projectedIndex >= 0) projected[projectedIndex] = mutation.container;
          return mutation;
        });
      },
    );
  }

  recordTemperatureReading(
    input: RecordTemperatureReadingInput,
    idempotencyKey = `temperature-reading:${randomId()}`,
  ) {
    return this.repository.recordTemperatureReading(input, idempotencyKey);
  }

  previewTemperatureIncident(
    snapshot: InventorySnapshot,
    input: DeclareTemperatureIncidentInput,
    declaredAt = new Date().toISOString(),
  ): string[] {
    return previewIncidentExposure(snapshot, input, declaredAt);
  }

  declareTemperatureIncident(
    input: DeclareTemperatureIncidentInput,
    idempotencyKey = `temperature-incident:${randomId()}`,
  ) {
    return this.repository.declareTemperatureIncident(input, idempotencyKey);
  }

  resolveIncidentExposure(
    input: ResolveIncidentExposureInput,
    idempotencyKey = `temperature-resolution:${randomId()}`,
  ) {
    return this.repository.resolveIncidentExposure(input, idempotencyKey);
  }

  async restoreBackup(snapshot: InventorySnapshot, expected?: InventorySnapshot): Promise<void> {
    await this.repository.replaceSnapshot(snapshot, expected);
  }

  private async seedDemo(resumeExistingSetup: boolean): Promise<InventorySnapshot> {
    const current = await this.snapshot();
    if (
      current.household != null &&
      (!resumeExistingSetup || current.meta.demoSeededAt != null)
    ) {
      throw new Error('La démonstration ne peut être chargée que dans un inventaire vide.');
    }
    const snapshot = current.household == null
      ? await this.repository.setup({
          householdName: 'Foyer démonstration',
          freezerName: 'Congélateur garage',
          equipmentKind: 'four-star',
          locationNames: ['Tiroir du haut', 'Tiroir du milieu', 'Tiroir du bas'],
          safetyAcknowledged: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris',
        })
      : current;
    const rules = this.horizonCatalog.list();
    const demos = createDemoContainers(
      snapshot.locations.map((location) => location.id),
      rules,
    );
    const containersKeptUnderReview = new Set<string>();
    for (const [index, demo] of demos.entries()) {
      const draft = await this.createContainer(demo.input, `public-demo:v1:container:${index}`);
      const confirmed = await this.confirm(draft.container);
      let current = confirmed.container;
      if (demo.partialRemaining != null && current.unit != null) {
        current = (
          await this.consumePart(current, {
            mode: 'remaining',
            value: demo.partialRemaining,
            unit: current.unit,
            replacementLocationId: current.locationId,
          })
        ).container;
      }
      if (demo.incident) containersKeptUnderReview.add(current.id);
    }
    if (demos.some((item) => item.incident)) {
      const incident = await this.declareTemperatureIncident(
        {
          freezerId: snapshot.freezers[0]!.id,
          kind: 'unknown',
          source: 'manual_observation',
          started: { value: null, certainty: 'unknown' },
          ended: { value: null, certainty: 'unknown' },
          maximumTemperatureC: null,
          note: 'Température inconnue — donnée fictive',
        },
        'public-demo:v1:temperature-incident',
      );
      for (const containerId of incident.affectedContainerIds) {
        if (containersKeptUnderReview.has(containerId)) continue;
        await this.resolveIncidentExposure(
          {
            incidentId: incident.id,
            containerId,
            authorLocal: 'Démonstration fictive',
            choice: 'cleared',
            comment: 'Décision fictive pour illustrer une file de vérification partiellement traitée.',
          },
          `public-demo:v1:temperature-resolution:${containerId}`,
        );
      }
    }
    await this.repository.saveDemoSeededAt(new Date().toISOString());
    return this.snapshot();
  }

  async loadDemo(): Promise<InventorySnapshot> {
    return this.seedDemo(false);
  }

  async ensurePublicDemo(): Promise<InventorySnapshot> {
    return this.seedDemo(true);
  }

  async resetDemo(): Promise<void> {
    const snapshot = await this.snapshot();
    if (snapshot.household == null || snapshot.meta.demoSeededAt == null) {
      throw new Error('Seule une démonstration fictive peut être réinitialisée ici.');
    }
    await this.deleteHousehold(snapshot.household.id);
  }

  async deleteHousehold(
    householdId: string,
    signal?: AbortSignal,
  ): Promise<HouseholdDeletionResult> {
    const snapshot = await this.repository.loadSnapshot();
    if (snapshot.household == null) {
      const pending = await this.repository.pendingHouseholdArtifactDeletions();
      if (pending.includes(householdId)) {
        await this.householdArtifacts.deleteForHousehold(householdId);
        await this.repository.completeHouseholdArtifactDeletion(householdId);
      }
      return { householdId, deleted: false };
    }
    if (snapshot.household.id !== householdId) {
      throw new Error('Le foyer demandé n’est pas le foyer actif. Aucune donnée supprimée.');
    }
    const result = await this.repository.deleteHousehold(householdId, signal);
    await this.householdArtifacts.deleteForHousehold(householdId);
    await this.repository.completeHouseholdArtifactDeletion(householdId);
    return result;
  }

  close(): void {
    this.repository.close();
  }
}
